import * as React from 'react'
import clsx from 'clsx'
import { SwitchBase, SwitchBaseProps } from '../SwitchBase'
import { useRadioGroup } from '../RadioGroup'
import { SxProps, sx as sxFunc } from '../sx'
import { RadioButtonIcon } from './RadioButtonIcon'
import style from './index.module.scss'
import { capitalize, createChainedFunction } from '../../utils'

export interface RadioClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the root element if `checked={true}`. */
  checked: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** Styles applied to the root element if `color="primary"`. */
  colorPrimary: string
  /** Styles applied to the root element if `color="secondary"`. */
  colorSecondary: string
}

export interface RadioProps
  extends Omit<SwitchBaseProps, 'checkedIcon' | 'color' | 'icon' | 'type'> {
  /**
   * The icon to display when the component is checked.
   * @default <RadioButtonIcon checked />
   */
  checkedIcon?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<RadioClasses>
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'default'

  /**
   * If `true`, the component is disabled.
   */
  disabled?: boolean
  /**
   * The icon to display when the component is unchecked.
   * @default <RadioButtonIcon />
   */
  icon?: React.ReactNode
  /**
   * The size of the component.
   * `small` is equivalent to the dense radio styling.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

function areEqualValues(a: any, b: any) {
  if (typeof b === 'object' && b !== null) {
    return a === b
  }

  // The value could be a number, the DOM will stringify it anyway.
  return String(a) === String(b)
}

const defaultCheckedIcon = <RadioButtonIcon checked />
const defaultIcon = <RadioButtonIcon />

export const Radio = React.forwardRef<HTMLButtonElement, RadioProps>(
  function Radio(props, ref) {
    const {
      checked: checkedProp,
      classes,
      checkedIcon = defaultCheckedIcon,
      color = 'primary',
      icon = defaultIcon,
      name: nameProp,
      onChange: onChangeProp,
      size = 'medium',
      className,
      disabled,
      sx,
      ...other
    } = props

    const radioGroup = useRadioGroup()

    let checked = checkedProp
    const onChange = createChainedFunction(
      // @ts-ignore
      onChangeProp,
      radioGroup && radioGroup.onChange
    )
    let name = nameProp

    if (radioGroup) {
      if (typeof checked === 'undefined') {
        checked = areEqualValues(radioGroup.value, props.value)
      }
      if (typeof name === 'undefined') {
        name = radioGroup.name
      }
    }

    return (
      <>
        <SwitchBase
          type="radio"
          icon={React.cloneElement(icon as JSX.Element, {
            size: defaultIcon.props.fontSize ?? size,
          })}
          checkedIcon={React.cloneElement(checkedIcon as JSX.Element, {
            size: defaultCheckedIcon.props.fontSize ?? size,
          })}
          classes={classes}
          name={name}
          checked={checked}
          onChange={onChange}
          ref={ref}
          className={clsx(
            style.root,
            classes?.root,
            // @ts-ignore
            style[`color--${color}`],
            // @ts-ignore
            classes?.[`color${capitalize(color)}`],
            style[`size--${size}`],
            checked && [style.checked],
            disabled && [style.disabled],
            className,
            sxFunc(sx)
          )}
          disabled={disabled}
          {...other}
        />
      </>
    )
  }
)
